// src/pages/AgendaLembretes.jsx
import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import SelectFuncionario from "../components/ui/selectFuncionario";
import "../sass/pages/agendaLembretes.scss";

/* =========================
   Config / Helpers
   ========================= */
const STORAGE_USER_KEY = "SSW_MSG_USER";
const DEFAULT_USER = Number(localStorage.getItem(STORAGE_USER_KEY) || 101);

// toInt compatível com MensagensInternas
const toInt = (v) => {
  if (typeof v === "bigint") return Number(v);
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : null;
};

// injeta x-user-id por requisição (mesmo padrão das mensagens internas)
const withUid = (uid) => ({ headers: { "x-user-id": uid } });

const toISO = (d) => (d instanceof Date ? d.toISOString().slice(0, 19) : d);

const fmtDate = (s) =>
  s
    ? new Date(s).toLocaleDateString("pt-BR", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
      })
    : "-";

const fmtDT = (s) =>
  s
    ? new Date(s).toLocaleString("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "-";

const today0 = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// ===== helpers id/nome (mesma lógica de MensagensInternas) =====
const pickName = (obj) =>
  obj?.NOME ??
  obj?.NOME_DEV ??
  obj?.NOME_FUNCIONARIO ??
  obj?.NOME_FUN ??
  obj?.NOME_COMPLETO ??
  obj?.NOME_USUARIO ??
  obj?.USUARIO_NOME ??
  obj?.AUTOR_NOME ??
  obj?.Nome ??
  obj?.nome ??
  null;

const normalizeBatchNames = (payload) => {
  if (!payload) return {};
  const out = {};

  if (payload.map && typeof payload.map === "object") {
    for (const [k, v] of Object.entries(payload.map)) {
      const id = toInt(k);
      const name = typeof v === "string" ? v : pickName(v);
      if (id && name) out[id] = String(name);
    }
    return out;
  }

  if (Array.isArray(payload)) {
    payload.forEach((x) => {
      const id = toInt(x?.IdFuncionario ?? x?.ID ?? x?.id);
      const name = pickName(x);
      if (id && name) out[id] = String(name);
    });
    return out;
  }

  if (typeof payload === "object") {
    for (const k of Object.keys(payload)) {
      const id = toInt(k);
      const v = payload[k];
      const name = typeof v === "string" ? v : pickName(v);
      if (id && name) out[id] = String(name);
    }
  }

  return out;
};

async function fetchNamesBatch(ids) {
  const uniq = Array.from(new Set(ids.map(toInt).filter(Boolean)));
  if (!uniq.length) return {};
  try {
    const { data } = await api.get("/func/names", {
      params: { ids: uniq.join(",") },
    });
    return normalizeBatchNames(data);
  } catch {
    return {};
  }
}

function getFuncId(f) {
  return toInt(f?.IdFuncionario ?? f?.id ?? f?.ID) || null;
}

// referência de data para filtros (mesma lógica usada no agrupamento)
function getRefDate(it) {
  const ref =
    it.tipo === "EVENTO"
      ? it.inicio || it.fim || it.created_at
      : it.due_at || it.created_at;
  return ref ? new Date(ref) : null;
}

/* =========================
   API wrapper
   ========================= */
const AgendaApi = {
  createItem: async (payload, uid) =>
    (await api.post(`/ssw/agenda/items`, payload, withUid(uid))).data,

  listItems: async ({ tipo, from, to, status, limit = 100 }, uid) => {
    const params = { limit };

    if (tipo && tipo !== "ALL") params.tipo = tipo;

    // status ATRASADO é tratado no front, não vai para o back
    if (status && status !== "ALL" && status !== "ATRASADO") {
      params.status = status;
    }

    if (from) params.from = toISO(from);
    if (to) params.to = toISO(to);

    const { data } = await api.get(`/ssw/agenda/items`, {
      params,
      ...withUid(uid),
    });
    return data;
  },

  addParticipantes: async ({ itemId, participantes }, uid) =>
    api.post(
      `/ssw/agenda/items/${itemId}/participantes`,
      { participantes },
      withUid(uid)
    ),

  responder: async ({ itemId, resposta }, uid) =>
    api.post(
      `/ssw/agenda/items/${itemId}/responder`,
      { resposta },
      withUid(uid)
    ),
};

/* =========================
   Página
   ========================= */
export default function AgendaLembretes() {
  const [userId, setUserId] = useState(DEFAULT_USER);
  const [peopleNames, setPeopleNames] = useState({});

  const getName = (id) =>
    id && peopleNames[id] ? peopleNames[id] : id ? `#${id}` : "-";
  const mergeNames = (obj) =>
    setPeopleNames((prev) => ({ ...prev, ...obj }));

  const [tipo, setTipo] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [from, setFrom] = useState(today0());
  const [to, setTo] = useState(addDays(today0(), 7));

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    tipo: "LEMBRETE",
    titulo: "",
    descricao: "",
    inicio: "",
    fim: "",
    allday: false,
    rrule: "",
    due_at: "",
    prioridade: "MEDIA",
    participantes: "",
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_USER_KEY, String(userId));
    (async () => {
      try {
        const map = await fetchNamesBatch([userId]);
        mergeNames(map);
      } catch {
        /* no-op */
      }
    })();
  }, [userId]);

  const quickRange = (preset) => {
    const t0 = today0();
    if (preset === "HOJE") {
      setFrom(t0);
      setTo(addDays(t0, 1));
    } else if (preset === "7D") {
      setFrom(t0);
      setTo(addDays(t0, 7));
    } else if (preset === "30D") {
      setFrom(t0);
      setTo(addDays(t0, 30));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await AgendaApi.listItems(
        {
          tipo,
          from,
          to,
          status,
          limit: 200,
        },
        userId
      );

      let effective = data || [];

      // Filtro derivado para "ATRASADO" (não é status físico da tabela)
      if (status === "ATRASADO") {
        const now = new Date();
        effective = effective.filter((it) => {
          const d = getRefDate(it);
          return d && d < now;
        });
      }

      setItems(effective);
    } catch (e) {
      console.error(e);
      alert(
        `Falha ao listar agenda: ${
          e?.response?.data?.message || e.message
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, status, from, to, userId]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const ref =
        it.tipo === "EVENTO"
          ? it.inicio || it.fim || it.created_at
          : it.due_at || it.created_at;
      const key = new Date(ref).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }

    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => {
        const aT = new Date(
          a.inicio || a.due_at || a.created_at
        ).getTime();
        const bT = new Date(
          b.inicio || b.due_at || b.created_at
        ).getTime();
        return aT - bT;
      });
      map.set(k, arr);
    }

    return Array.from(map.entries())
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([key, arr]) => ({ dateKey: key, items: arr }));
  }, [items]);

  const openCreate = () => {
    setForm({
      tipo: "LEMBRETE",
      titulo: "",
      descricao: "",
      inicio: "",
      fim: "",
      allday: false,
      rrule: "",
      due_at: "",
      prioridade: "MEDIA",
      participantes: "",
    });
    setDlgOpen(true);
  };

  const createItem = async () => {
    if (!form.titulo?.trim()) return alert("Título é obrigatório.");
    if (!["LEMBRETE", "TAREFA", "EVENTO"].includes(form.tipo))
      return alert("Tipo inválido.");

    const payload = {
      tipo: form.tipo,
      titulo: form.titulo.trim(),
      descricao: form.descricao?.trim() || undefined,
      prioridade: form.prioridade || "MEDIA",
    };

    if (form.tipo === "EVENTO") {
      if (!form.allday && (!form.inicio || !form.fim)) {
        return alert(
          "Para EVENTO, informe início e fim (ou marque Dia Inteiro)."
        );
      }
      payload.allday = !!form.allday;
      if (form.inicio) payload.inicio = form.inicio;
      if (form.fim) payload.fim = form.fim;
      if (form.rrule?.trim()) payload.rrule = form.rrule.trim();
    } else {
      if (!form.due_at)
        return alert("Informe a data/hora do vencimento (due_at).");
      payload.due_at = form.due_at;
    }

    if (form.participantes?.trim()) {
      const parts = form.participantes
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isInteger(n) && n > 0);
      if (parts.length) payload.participantes = parts;
    }

    setCreating(true);
    try {
      await AgendaApi.createItem(payload, userId);
      setDlgOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      alert(
        `Falha ao criar item: ${
          e?.response?.data?.message || e.message
        }`
      );
    } finally {
      setCreating(false);
    }
  };

  const addParticipantes = async (itemId) => {
    const raw = window.prompt(
      "Informe IDs a adicionar (separados por vírgula)",
      ""
    );
    if (!raw) return;
    const parts = raw
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && n > 0);
    if (!parts.length) return alert("Nenhum ID válido.");
    try {
      await AgendaApi.addParticipantes(
        { itemId, participantes: parts },
        userId
      );
      await load();
    } catch (e) {
      console.error(e);
      alert(
        `Falha ao adicionar participantes: ${
          e?.response?.data?.message || e.message
        }`
      );
    }
  };

  const responder = async (itemId, resposta) => {
    try {
      await AgendaApi.responder({ itemId, resposta }, userId);
      await load();
    } catch (e) {
      console.error(e);
      alert(
        `Falha ao responder: ${
          e?.response?.data?.message || e.message
        }`
      );
    }
  };

  return (
    <MainLayout>
      <div className="ag">
        {/* Topbar */}
        <div className="ag__top">
          <div className="ag__left">
            <UserSwitcher
              userId={userId}
              onChange={setUserId}
              getName={getName}
              mergeNames={mergeNames}
            />
            <button className="btn btn--ghost" onClick={load}>
              Atualizar
            </button>
          </div>

          <div className="ag__filters">
            <div className="ag__row">
              <span className="lbl">Tipo</span>
              <select
                className="input input--sm"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
              >
                {["ALL", "LEMBRETE", "TAREFA", "EVENTO"].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>

              <span className="lbl">Status</span>
              <select
                className="input input--sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {[
                  "ALL",
                  "ABERTO",
                  "EM_ANDAMENTO",
                  "CONCLUIDO",
                  "ATRASADO",
                  "CANCELADO",
                ].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>

              <span className="lbl">Janela</span>
              <input
                type="datetime-local"
                className="input input--sm"
                value={toLocalInput(from)}
                onChange={(e) => setFrom(new Date(e.target.value))}
              />
              <span className="lbl">→</span>
              <input
                type="datetime-local"
                className="input input--sm"
                value={toLocalInput(to)}
                onChange={(e) => setTo(new Date(e.target.value))}
              />

              <div className="ag__quick">
                {["HOJE", "7D", "30D"].map((k) => (
                  <button
                    key={k}
                    className="chip"
                    onClick={() => quickRange(k)}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="ag__right">
            <button className="btn btn--primary" onClick={openCreate}>
              Novo item
            </button>
          </div>
        </div>

        {/* Lista */}
        <div className="ag__content">
          {loading ? (
            <div className="muted pad">Carregando...</div>
          ) : grouped.length === 0 ? (
            <div className="muted pad">
              Nenhum item na janela selecionada.
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.dateKey} className="ag__group">
                <div className="ag__groupHeader">
                  {fmtDate(g.dateKey)}
                </div>
                <ul className="list">
                  {g.items.map((it) => (
                    <AgendaRow
                      key={it.id}
                      it={it}
                      onAdd={() => addParticipantes(it.id)}
                      onResp={(r) => responder(it.id, r)}
                    />
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>

        {/* Diálogo: Novo Item */}
        {dlgOpen && (
          <div className="dlg">
            <div className="dlg__panel">
              <div className="dlg__header">
                <h3>Novo item</h3>
                <button
                  className="btn btn--ghost"
                  onClick={() => setDlgOpen(false)}
                >
                  Fechar
                </button>
              </div>

              <div className="dlg__body">
                <div className="grid2">
                  <div>
                    <label className="lbl">Tipo</label>
                    <select
                      className="input"
                      value={form.tipo}
                      onChange={(e) =>
                        setForm((s) => ({ ...s, tipo: e.target.value }))
                      }
                    >
                      {["LEMBRETE", "TAREFA", "EVENTO"].map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="lbl">Prioridade</label>
                    <select
                      className="input"
                      value={form.prioridade}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          prioridade: e.target.value,
                        }))
                      }
                    >
                      {["BAIXA", "MEDIA", "ALTA", "CRITICA"].map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <label className="lbl">Título</label>
                <input
                  className="input"
                  placeholder="Ex.: Revisar contratos"
                  value={form.titulo}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      titulo: e.target.value,
                    }))
                  }
                />

                <label className="lbl">Descrição</label>
                <textarea
                  className="textarea"
                  placeholder="Detalhes (opcional)"
                  value={form.descricao}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      descricao: e.target.value,
                    }))
                  }
                />

                {form.tipo === "EVENTO" ? (
                  <>
                    <div className="grid2">
                      <div>
                        <label className="lbl">Início</label>
                        <input
                          type="datetime-local"
                          className="input"
                          value={form.inicio}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              inicio: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className="lbl">Fim</label>
                        <input
                          type="datetime-local"
                          className="input"
                          value={form.fim}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              fim: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="grid2">
                      <div className="row">
                        <input
                          id="ck-allday"
                          type="checkbox"
                          checked={!!form.allday}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              allday: e.target.checked,
                            }))
                          }
                        />
                        <label
                          htmlFor="ck-allday"
                          className="lbl lbl--inline"
                        >
                          Dia inteiro
                        </label>
                      </div>
                      <div>
                        <label className="lbl">RRULE (opcional)</label>
                        <input
                          className="input"
                          placeholder="FREQ=DAILY;COUNT=5"
                          value={form.rrule}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              rrule: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="lbl">
                      Vencimento (due_at)
                    </label>
                    <input
                      type="datetime-local"
                      className="input"
                      value={form.due_at}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          due_at: e.target.value,
                        }))
                      }
                    />
                  </>
                )}

                <label className="lbl">
                  Participantes (IDs separados por vírgula) — opcional
                </label>
                <input
                  className="input"
                  placeholder="Ex.: 202,303"
                  value={form.participantes}
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      participantes: e.target.value,
                    }))
                  }
                />

                <div className="dlg__actions">
                  <button
                    className="btn btn--primary"
                    onClick={createItem}
                    disabled={creating}
                  >
                    {creating ? "Criando..." : "Criar"}
                  </button>
                  <button
                    className="btn btn--ghost"
                    onClick={() => setDlgOpen(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

/* =========================
   Subcomponentes
   ========================= */
function AgendaRow({ it, onAdd, onResp }) {
  const isEvento = it.tipo === "EVENTO";
  const when = isEvento
    ? it.allday
      ? `${fmtDate(it.inicio || it.fim)} (dia inteiro)`
      : `${fmtDT(it.inicio)} → ${fmtDT(it.fim)}`
    : `Vence em: ${fmtDT(it.due_at)}`;

  return (
    <li className={`agItem agItem--${it.tipo.toLowerCase()}`}>
      <div className="agItem__left">
        <span className="chip">{it.tipo}</span>
        <span
          className={`chip chip--prio prio--${
            (it.prioridade || "MEDIA").toLowerCase()
          }`}
        >
          {it.prioridade || "MEDIA"}
        </span>
        <span
          className={`chip chip--status st--${
            (it.status || "ABERTO").toLowerCase()
          }`}
        >
          {it.status || "ABERTO"}
        </span>
      </div>

      <div className="agItem__main">
        <div className="agItem__title">{it.titulo}</div>
        <div className="agItem__desc">{it.descricao || "-"}</div>
        <div className="agItem__when">{when}</div>
        {it.rrule ? (
          <div className="agItem__rrule">RRULE: {it.rrule}</div>
        ) : null}
      </div>

      <div className="agItem__actions">
        {isEvento ? (
          <div className="agItem__btns">
            <button
              className="btn btn--ghost"
              onClick={() => onResp("ACEITO")}
            >
              Aceitar
            </button>
            <button
              className="btn btn--ghost"
              onClick={() => onResp("TALVEZ")}
            >
              Talvez
            </button>
            <button
              className="btn btn--ghost"
              onClick={() => onResp("RECUSADO")}
            >
              Recusar
            </button>
          </div>
        ) : null}
        <button className="btn btn--ghost" onClick={onAdd}>
          + Participantes
        </button>
      </div>
    </li>
  );
}

function UserSwitcher({ userId, onChange, getName, mergeNames }) {
  return (
    <div className="user-switcher">
      <span className="lbl">Usuário</span>
      <div className="user-switcher__row">
        <span className="user-switcher__name">
          {getName(userId)}
          {userId ? (
            <span className="user-switcher__id">#{userId}</span>
          ) : null}
        </span>

        <SelectFuncionario
          placeholder="Trocar usuário…"
          onChange={async (func) => {
            if (!func) return;
            const id = getFuncId(func);
            if (!id) return;
            onChange(id);
            try {
              const map = await fetchNamesBatch([id]);
              mergeNames(map);
            } catch {
              /* ignore */
            }
          }}
          defaultOptions={false}
          minChars={2}
          includeBloqueados={false}
        />
      </div>
    </div>
  );
}

/* =========================
   Utils
   ========================= */
function toLocalInput(d) {
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  return `${y}-${m}-${day}T${hh}:${mm}`;
}
